import { useEffect, useRef, useState } from "react";

type PhoneEntry = {
  label: string;
  number: string;
};

type Props = {
  customerName: string;
  phones: PhoneEntry[];
  onSendText?: () => void;
};

export default function CallCustomerPopover({
  customerName,
  phones,
  onSendText,
}: Props) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const copy = async (num: string) => {
    try {
      await navigator.clipboard.writeText(num);
      setCopied(num);
      setTimeout(() => setCopied((c) => (c === num ? null : c)), 1600);
    } catch {
      // ignore
    }
  };

  const validPhones = phones.filter((p) => !!p.number);
  const firstNumber = validPhones[0]?.number;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold border cursor-pointer whitespace-nowrap transition-colors ${
          open
            ? "border-ink-900 bg-ink-900 text-white"
            : "border-ink-200 bg-white text-ink-700 hover:bg-cream-100"
        }`}
      >
        <i className="ri-phone-line"></i>
        Call Customer
        <i
          className={`ri-arrow-down-s-line text-sm transition-transform ${
            open ? "rotate-180" : ""
          }`}
        ></i>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-ink-200 rounded-lg z-30 overflow-hidden">
          <div className="px-4 py-3 border-b border-ink-100 bg-cream-50">
            <div className="text-[10px] uppercase tracking-wider text-ink-400 font-bold">
              Call
            </div>
            <div className="text-sm font-bold text-ink-900 truncate">
              {customerName}
            </div>
          </div>

          <ul className="divide-y divide-ink-100">
            {validPhones.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-ink-500">
                No phone number on file.
              </li>
            )}
            {validPhones.map((p) => (
              <li key={p.number} className="px-4 py-3">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-ink-400 font-bold">
                    {p.label}
                  </span>
                  <button
                    onClick={() => copy(p.number)}
                    className="inline-flex items-center gap-1 text-[10px] font-bold text-ink-500 hover:text-ink-900 cursor-pointer whitespace-nowrap"
                  >
                    <i
                      className={
                        copied === p.number
                          ? "ri-check-line text-emerald-600"
                          : "ri-file-copy-line"
                      }
                    ></i>
                    {copied === p.number ? "Copied" : "Copy"}
                  </button>
                </div>
                <a
                  href={`tel:${p.number.replace(/[^0-9+]/g, "")}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-ink-200 bg-white hover:bg-cream-100 cursor-pointer group"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
                      <i className="ri-phone-fill text-sm"></i>
                    </span>
                    <span className="text-sm font-semibold text-ink-900 truncate tabular-nums">
                      {p.number}
                    </span>
                  </span>
                  <span className="text-[11px] font-bold text-emerald-700 group-hover:text-emerald-800 whitespace-nowrap">
                    Call now
                  </span>
                </a>
              </li>
            ))}
          </ul>

          {(onSendText || firstNumber) && (
            <div className="border-t border-ink-100 bg-cream-50 px-4 py-2.5 flex items-center justify-between gap-2">
              <span className="text-[11px] text-ink-500">
                Can't reach them?
              </span>
              {onSendText && (
                <button
                  onClick={() => {
                    setOpen(false);
                    onSendText();
                  }}
                  className="inline-flex items-center gap-1 text-xs font-bold text-ink-900 hover:text-ink-700 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-message-2-line"></i>
                  Send text instead
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}